import ApiService from './api';
import { safeArray } from '../utils/money';

// Webhook endpoint 配置
export interface WebhookEndpoint {
  url: string;
  description: string;
  event_types: string[]; // 空数组表示订阅全部事件
  active: boolean;
}

// 重试策略
export interface WebhookRetryPolicy {
  max_retries: number;
  retry_interval: number;
}

// Webhook 设置（GET/PUT 主体）
export interface WebhookSettings {
  endpoints: WebhookEndpoint[];
  secret_key: string;
  event_types: string[];
  enabled: boolean;
  retry_policy: WebhookRetryPolicy;
}

// 事件目录中的单个事件定义
export interface WebhookEventDef {
  type: string;
  category: string;
  description: string;
}

// 测试事件返回结果
export interface WebhookTestResult {
  success?: boolean;
  response_status?: number;
  status_code?: number;
  response_body?: string;
  duration_ms?: number;
  error?: string;
  [key: string]: any;
}

// 投递日志
export interface WebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  endpoint: string;
  response_status: number;
  success: boolean;
  attempt_number: number;
  error?: string;
  duration_ms: number;
  created_at: string;
  payload?: string;
  response_body?: string;
}

export interface PaginatedMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const DEFAULT_SETTINGS: WebhookSettings = {
  endpoints: [],
  secret_key: '',
  event_types: [],
  enabled: false,
  retry_policy: { max_retries: 3, retry_interval: 60 },
};

export class WebhookService {
  /**
   * 获取 Webhook 设置
   */
  static async getSettings(): Promise<WebhookSettings> {
    const response = await ApiService.get<WebhookSettings>('/merchant/settings/webhooks');
    const data = response.data || ({} as WebhookSettings);
    return {
      ...DEFAULT_SETTINGS,
      ...data,
      endpoints: safeArray<WebhookEndpoint>(data.endpoints),
      event_types: safeArray<string>(data.event_types),
      retry_policy: data.retry_policy || DEFAULT_SETTINGS.retry_policy,
    };
  }

  /**
   * 更新 Webhook 设置。
   * 若 secret_key 提交空字符串，后端会生成新的密钥。
   */
  static async updateSettings(settings: WebhookSettings): Promise<WebhookSettings> {
    const response = await ApiService.put<WebhookSettings>(
      '/merchant/settings/webhooks',
      settings
    );
    const data = response.data || settings;
    return {
      ...DEFAULT_SETTINGS,
      ...data,
      endpoints: safeArray<WebhookEndpoint>(data.endpoints),
      event_types: safeArray<string>(data.event_types),
      retry_policy: data.retry_policy || DEFAULT_SETTINGS.retry_policy,
    };
  }

  /**
   * 发送一条测试事件到指定 URL
   */
  static async sendTest(url: string, eventType: string): Promise<WebhookTestResult> {
    const response = await ApiService.post<WebhookTestResult>(
      '/merchant/settings/webhooks/test',
      { url, event_type: eventType }
    );
    return response.data || {};
  }

  /**
   * 获取事件目录（47 个事件，按 category 分组）
   */
  static async getEventCatalog(): Promise<WebhookEventDef[]> {
    const response = await ApiService.get<{ events: WebhookEventDef[] }>(
      '/merchant/settings/webhooks/event-catalog'
    );
    return safeArray<WebhookEventDef>(response.data?.events);
  }

  /**
   * 获取投递日志（分页）
   */
  static async listDeliveries(
    page: number = 1,
    perPage: number = 20
  ): Promise<{ data: WebhookDelivery[]; meta: PaginatedMeta }> {
    const response = await ApiService.get<WebhookDelivery[]>(
      '/merchant/settings/webhooks/deliveries',
      { page, per_page: perPage }
    );
    return {
      data: safeArray<WebhookDelivery>(response.data),
      meta:
        (response.meta as PaginatedMeta) || {
          total: 0,
          per_page: perPage,
          current_page: page,
          last_page: 1,
        },
    };
  }
}

export default WebhookService;
